import { useStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { createStore } from 'zustand/vanilla'

interface GolfServiceState {
  /** Slot ID currently open in a Sheet editing panel, or null if none */
  editingSlotId: string | null
}

interface GolfServiceActions {
  /** Open the Sheet editing panel for a golf slot */
  startEditingSlot: (slotId: string) => void
  /** Close the Sheet editing panel for a golf slot */
  endEditingSlot: (slotId: string) => void
}

type GolfServiceStore = GolfServiceState & GolfServiceActions

export function createGolfServiceStore(initState: GolfServiceState) {
  return createStore<GolfServiceStore>()(
    immer(set => ({
      ...initState,
      startEditingSlot(slotId) {
        set((state) => {
          state.editingSlotId = slotId
        })
      },
      endEditingSlot(slotId) {
        set((state) => {
          if (state.editingSlotId === slotId)
            state.editingSlotId = null
        })
      },
    })),
  )
}

export const golfServiceStore = createGolfServiceStore({ editingSlotId: null })

/** Returns true when a given slot ID has its Sheet panel open */
export function golfSlotIsBeingEdited(slotId: string) {
  return (state: GolfServiceState) =>
    state.editingSlotId === slotId
}

export function getGolfSlotIdBeingEdited(state: GolfServiceState): string | null {
  return state.editingSlotId
}

type StoreSelector<T, S> = (state: S) => T

interface UseBoundStore<S> {
  (): S
  <T>(selector: StoreSelector<T, S>): T
}

export const useGolfServiceStore: UseBoundStore<GolfServiceStore> = <T>(selector?: StoreSelector<T, GolfServiceStore>) => {
  const store = useStore(golfServiceStore)
  if (!selector)
    return store as T
  return selector(store)
}
